import { apiFetch, ApiError } from './client'
import {
  orderReviewSchema,
  dropshipResponseSchema,
  type OrderReview,
  type DropshipRequest,
  type DropshipResponse,
} from '@/lib/schemas/orderReview'

/**
 * Error thrown by submitDropship. Carries the structured `detail.code` from the
 * backend so the UI can special-case KH_ARTICLE_NOT_FOUND / ALREADY_SUBMITTED.
 * The detail body may also be a plain string (auth/5xx) — handled below.
 */
export class DropshipSubmitError extends Error {
  status: number
  code?: string
  selectedTotal?: string
  constructor(status: number, message: string, code?: string, selectedTotal?: string) {
    super(message)
    this.name = 'DropshipSubmitError'
    this.status = status
    this.code = code
    this.selectedTotal = selectedTotal
  }
}

/** GET /api/queue/{shopifyOrderId} — full order detail for the review screen. */
export async function getOrderReview(shopifyOrderId: string): Promise<OrderReview> {
  const res = await apiFetch(`/api/queue/${shopifyOrderId}`, { method: 'GET' })
  if (!res.ok) {
    const detail = await readStringDetail(res)
    throw new ApiError(res.status, detail ?? `Failed to load order (${res.status})`, detail)
  }
  return orderReviewSchema.parse(await res.json())
}

/** POST /api/dropship — submit selected line items to KH. 201 on success. */
export async function submitDropship(payload: DropshipRequest): Promise<DropshipResponse> {
  const res = await apiFetch('/api/dropship', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    throw await readSubmitError(res)
  }
  return dropshipResponseSchema.parse(await res.json())
}

async function readStringDetail(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.json()) as { detail?: unknown }
    return typeof body?.detail === 'string' ? body.detail : undefined
  } catch {
    return undefined
  }
}

async function readSubmitError(res: Response): Promise<DropshipSubmitError> {
  let code: string | undefined
  let message: string | undefined
  let selectedTotal: string | undefined
  try {
    const body = (await res.json()) as { detail?: unknown }
    const detail = body?.detail
    if (typeof detail === 'string') {
      message = detail
    } else if (detail && typeof detail === 'object') {
      const obj = detail as Record<string, unknown>
      code = typeof obj.code === 'string' ? obj.code : undefined
      message = typeof obj.message === 'string' ? obj.message : undefined
      selectedTotal = typeof obj.selected_total === 'string' ? obj.selected_total : undefined
    }
  } catch {
    // non-JSON body — fall through to generic message
  }
  return new DropshipSubmitError(
    res.status,
    message ?? `Submission failed (${res.status})`,
    code,
    selectedTotal,
  )
}
